import { useContext, useState } from 'react'
import cx from 'classnames'

import { type SanityFilterGroup } from '@data/sanity/queries/types/cart'
import { helvetica } from '@lib/fonts'
import { type UpdateUrlParameters } from '@lib/hooks'
import { type Filter } from '@lib/product'
import { StringsContext } from '@lib/strings-context'

import Button, { ButtonVariant } from '@components/buttons/button'
import Drawer, { DrawerDirection } from '@components/drawer'
import Icon from '@components/icon'
import CollectionFilterGroup from './filter-group'

interface CollectionFilterProps {
  filterGroups: SanityFilterGroup[]
  activeFilters: Filter[]
  activeFilterValueCount: number
  itemTotal: number
  updateParameters: UpdateUrlParameters
}

const CollectionFilter = ({
  filterGroups,
  activeFilters = [],
  activeFilterValueCount,
  itemTotal,
  updateParameters,
}: CollectionFilterProps) => {
  const strings = useContext(StringsContext)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const clearFilters = () => {
    updateParameters(
      activeFilters.map((activeFilter) => ({
        name: activeFilter.name,
        value: null,
      })),
    )
  }

  return (
    <>
      <button
        onClick={() => setIsDrawerOpen(true)}
        className="relative text-left text-xs uppercase inline-flex items-center gap-x-2 leading-none"
      >
        {strings.collectionShowFiltersLabel}{' '}
        {activeFilterValueCount > 0 ? `(${activeFilterValueCount})` : ''}
        <Icon id="filter-button" name="Filter" className="shrink-0" />
      </button>

      <Drawer
        direction={DrawerDirection.LEFT}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        className={cx('max-w-md sm:max-w-sm font-sans', helvetica.variable)}
      >
        <div className="container flex gap-x-2 items-center justify-between py-6">
          <div className="flex items-center is-h3">
            {strings.collectionFilters}{' '}
            {activeFilterValueCount > 0 ? `(${activeFilterValueCount})` : ''}
          </div>

          <div className="flex items-center gap-x-6">
            {activeFilterValueCount > 0 && (
              <Button
                variant={ButtonVariant.LINK}
                onClick={clearFilters}
                className="uppercase"
              >
                {strings.collectionClearAllLabel}
              </Button>
            )}

            <button className="" onClick={() => setIsDrawerOpen(false)}>
              <Icon id="close-filters" name="Cross" className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <div className="flex-1 flex flex-col overflow-y-auto">
          {filterGroups.map((group) => (
            <CollectionFilterGroup
              key={group._key}
              group={group}
              activeFilter={activeFilters.find(
                (activeFilter) => activeFilter.name === group.slug.current,
              )}
              updateParameters={updateParameters}
            />
          ))}
        </div>

        <div className="container py-4 border-t border-gray">
          <Button
            variant={ButtonVariant.FILLED}
            disabled={itemTotal === 0}
            onClick={() => setIsDrawerOpen(false)}
            className="w-full"
          >
            {itemTotal > 0
              ? strings.collectionFilterResultsLabel?.replace(
                  /{count}/gi,
                  `${itemTotal}`,
                )
              : strings.collectionFilterNoResultsLabel}
          </Button>
        </div>
      </Drawer>
    </>
  )
}

export default CollectionFilter
